import React, { useRef, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Overlay,
  Popover,
  PopoverContent,
  Row,
} from "react-bootstrap";
import { Field, Formik } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { formikInput } from "./utils";
import uuidv4 from "uuid/v4";

export const EditableFieldAmount = ({
  value,
  onSave,
  placement,
}: {
  value;
  onSave;
  placement?: string;
}) => {
  return (
    <EditableField
      placement={placement}
      value={value}
      onSave={onSave}
      validationSchema={Yup.object({
        value: Yup.number().required(), // Basic non-empty validation
      })}
    />
  );
};

export const EditableFieldDateYYYYMMDD = ({
  value,
  onSave,
  placement,
}: {
  value;
  onSave;
  placement?: string;
}) => {
  return (
    <EditableField
      placement={placement}
      value={value}
      onSave={onSave}
      validationSchema={Yup.object().shape({
        value: Yup.string()
          .required()
          .matches(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
          .test("isValidDate", "Date is invalid or out of range", (value) => {
            if (!value) return false; // Skip validation if value is not provided

            const parts = value.split("-");
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);

            // Check if month and day are within the correct range
            if (month < 1 || month > 12 || day < 1 || day > 31) {
              return false;
            }

            // Attempt to construct a date and check if it's valid
            const date = new Date(year, month - 1, day);
            return (
              date.getFullYear() === year &&
              date.getMonth() + 1 === month &&
              date.getDate() === day
            );
          }),
      })}
    />
  );
};

export const EditableFieldRequiredText = ({
  value,
  onSave,
  placement,
}: {
  value;
  onSave;
  placement?: string;
}) => {
  return (
    <EditableField
      placement={placement}
      value={value}
      onSave={onSave}
      validationSchema={Yup.object({
        value: Yup.string().required(), // Basic non-empty validation
      })}
    />
  );
};

export const EditableFieldText = ({
  value,
  onSave,
  placement,
}: {
  value;
  onSave;
  placement?: string;
}) => {
  return (
    <EditableField
      placement={placement}
      value={value}
      onSave={onSave}
      validationSchema={Yup.object({
        value: Yup.string(), // Basic non-empty validation
      })}
    />
  );
};

export const EditableField = ({
  value,
  onSave,
  validationSchema,
  placement,
}: {
  value;
  onSave;
  validationSchema;
  placement?: string;
}) => {
  const [editing, setEditing] = useState(false);

  const [value2, setValue2] = React.useState(value);
  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    onSave(values.value);
    setSubmitting(false);
    setEditing(false);
    resetForm();
    setValue2(values.value);
  };
  const target = useRef(null);

  return (
    <>
      <div className="editable-text-icon-container">
        <span ref={target} className="editable-text-truncate-text">
          {value2}
        </span>

        <FontAwesomeIcon
          className="editable-text-fas"
          icon="edit"
          onClick={() => setEditing(true)}
        />
      </div>

      <Overlay
        show={editing}
        target={target.current}
        placement={placement}
        containerPadding={20} // Optional: Padding for the overlay container
      >
        <Popover id={`edit-field-popover-${uuidv4()}`}>
          {/* <Popover.Title as="h3">Edit Field</Popover.Title> */}
          <PopoverContent>
            <Formik
              initialValues={{ value: value }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <Form onSubmit={handleSubmit}>
                  {/* <Modal.Header closeButton>
                    <Modal.Title>Edit Value</Modal.Title>
                  </Modal.Header> */}
                  {/* <Modal.Body> */}
                  <Form.Group>
                    {/* <Form.Label>New Value</Form.Label> */}

                    <Field
                      // className="ml-2"
                      type="text"
                      id="value"
                      name="value"
                      component={formikInput}
                    />
                  </Form.Group>
                  {/* </Modal.Body> */}
                  <Row className="justify-content-end">
                    <Button
                      className="mr-2"
                      variant="secondary"
                      onClick={() => setEditing(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="mr-2"
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Save
                    </Button>
                  </Row>
                </Form>
              )}
            </Formik>
          </PopoverContent>
        </Popover>
      </Overlay>
    </>
  );
};

EditableField.defaultProps = {
  placement: "right",
};
